import * as sentry from "@sentry/angular";

import { AWSAuthService } from "@intocare-medapp/aws/http";
import { IcOrganisation } from "@intocare-medapp/organisation/ic-organisation";

import { CommonErrorHandler } from "./common-error-handler";
import { ErrorHandlerOptions } from "../entities/error-handler-options";
import { HttpErrorResponse } from "@angular/common/http";
import { Router } from "@angular/router";
import { ContextNotInitializedError } from "app/shared/services/context.service";

/** List of error status codes that shouldn't be reported to Sentry. */
const errorCodesToIgnore = new Set([0, 401, 403]);

export class RavenErrorHandler extends CommonErrorHandler {
	constructor(
		options: ErrorHandlerOptions,
		private awsAuthService: AWSAuthService,
		private icOrganisation: IcOrganisation,
		private router: Router,
	) {
		super();

		const { sentryReplaySessionSampleRate, sentryReplayOnErrorSampleRate } = options;

		sentry.init({
			dsn: options.url,
			environment: options.environment,
			release: options.version,
			ignoreErrors: [
				// Auth is expired - not an error
				"Local storage is missing an ID Token, Please authenticate",
				"Refresh Token has expired",
				// IoT connection closed - will reconnect automatically - not an error
				/^Connection closed$/,
				// Caused by amazon-cognito-identity-js
				/^Network error$/,
			],
			integrations: [sentry.extraErrorDataIntegration()],
			transport: sentry.makeBrowserOfflineTransport(sentry.makeFetchTransport),
			...(typeof sentryReplaySessionSampleRate === "number" && {
				replaySessionSampleRate: sentryReplaySessionSampleRate,
			}),
			...(typeof sentryReplayOnErrorSampleRate === "number" && {
				replaysOnErrorSampleRate: sentryReplayOnErrorSampleRate,
			}),
			beforeSend(event, hint) {
				if (hint?.originalException instanceof HttpErrorResponse) {
					if (hint.originalException.status === 0) {
						return null;
					}
				}
				return event;
			},
		});
	}

	transformToError(err: unknown) {
		if (err instanceof Error) {
			return err;
		}

		if (err instanceof HttpErrorResponse) {
			return new Error(err.message);
		}

		return new Error("Non-native error");
	}

	isUnrecoverableError(err: any) {
		if (err instanceof ContextNotInitializedError) {
			return true;
		}

		if (typeof err === "string" && err.includes("InvalidStateError")) {
			return true;
		}

		if (typeof err === "string" && err.includes("IndexedDB: UnknownError")) {
			return true;
		}

		return false;
	}

	override handleError(err: any) {
		if (this.isUnrecoverableError(err)) {
			this.router.navigate(["/something-went-wrong"]);
		}

		if (errorCodesToIgnore.has(err?.status)) {
			throw err;
		}

		const user = this.awsAuthService.getUser();
		const organisation = this.icOrganisation.getRawOrganisation();

		sentry.setUser({ id: (Boolean(user) && user.getUsername()) || "unknown" });
		sentry.setTags({
			organisation,
		});

		const error = this.transformToError(err);

		// Set context for non-native errors, since the cause is not shown in Sentry
		if (!(err instanceof Error)) {
			sentry.setContext("non-native error", err);
		}

		sentry.captureException(error);

		// Rethrow error so the error gets swallowed instead of reporting infinite. -> Workaround to fix infinite loop https://github.com/angular/angular/issues/17010
		// To reproduce this infinite loop error reporting add an undefined object and access a property on it in the template by example SettingsComponent {{ a.a.a }} .
		throw err;
	}
}
